/**
 * User Statistics Service - Phase 4 Architecture
 *
 * Encapsulates user-level stats and aggregate user statistics.
 * Extracted from UserService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import type { IService } from "@/services/core/service-registry";
import { ServiceRegistry } from "@/services/core/service-registry";
import { AuditService } from "./auditService";
import { UserStats } from "./types";

export class UserStatisticsService implements IService {
  readonly name = "user-statistics" as const;
  private readonly supabase = typedSupabase;
  private auditService?: AuditService;
  private registry: ServiceRegistry;

  constructor(serviceRegistry: ServiceRegistry) {
    this.registry = serviceRegistry;
  }

  async initialize(): Promise<void> {
    logger.info("📊 UserStatisticsService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("📊 UserStatisticsService disposed");
  }

  private async ensureAudit(): Promise<AuditService> {
    this.auditService ??= await this.registry.get<AuditService>("audit");
    return this.auditService;
  }

  async getUserStats(userId: string): Promise<UserStats | undefined> {
    const { data: posts, error: postsError } = await this.supabase
      .from("posts")
      .select("count", { count: "exact" })
      .eq("user_id", userId);

    const { data: comments, error: commentsError } = await this.supabase
      .from("comments")
      .select("count", { count: "exact" })
      .eq("user_id", userId);

    const { data: likes, error: likesError } = await this.supabase
      .from("likes")
      .select("count", { count: "exact" })
      .eq("user_id", userId);

    if (postsError || commentsError || likesError) {
      logger.error(
        "Error fetching user stats:", { error: postsError,
        commentsError,
        likesError
       });
      return undefined;
    }

    return {
      posts: posts ? { count: posts[0].count || 0 } : undefined,
      comments: comments ? { count: comments[0].count || 0 } : undefined,
      likes: likes ? { count: likes[0].count || 0 } : undefined,
    };
  }

  async getUserStatistics(): Promise<{
    totalUsers: number;
    activeUsers: number;
    suspendedUsers: number;
    newUsersThisMonth: number;
  }> {
    const { count: totalUsers, error: totalError } = await this.supabase
      .from("profiles")
      .select("*", { count: "exact", head: true });

    const { count: suspendedUsers, error: suspendedError } = await this.supabase
      .from("profiles")
      .select("*", { count: "exact", head: true })
      .eq("is_suspended", true);

    const { count: activeUsers, error: activeError } = await this.supabase
      .from("profiles")
      .select("*", { count: "exact", head: true })
      .eq("is_suspended", false);

    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const { count: newUsers, error: newError } = await this.supabase
      .from("profiles")
      .select("*", { count: "exact", head: true })
      .gte("created_at", startOfMonth.toISOString());

    if (totalError || suspendedError || activeError || newError) {
      logger.error(
        "Error fetching user statistics:", { error: totalError,
        suspendedError,
        activeError,
        newError
       });
      throw new Error("Failed to fetch user statistics");
    }

    return {
      totalUsers: totalUsers ?? 0,
      activeUsers: activeUsers ?? 0,
      suspendedUsers: suspendedUsers ?? 0,
      newUsersThisMonth: newUsers ?? 0,
    };
  }
}
